import { LANGUAGE_DEFAULT, NError, NSuccess } from '@configs/const.config'
import { TResApi, TResApiErr, TResDataListApi } from '@configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import { notification } from 'antd'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { TGame, TLeaderboard, TPatchGame, TQueryGame, TQueryLoaderByBoardGame } from '@src/modules'
import {
  TPatchWheelGame,
  TQueryWheelGame,
  TQueryWheelGameUser,
  TWheelGame,
  TWheelGameItem,
  TWheelGameUser,
} from '@src/modules/game-wheel'

import { queryClient } from '..'
import {
  createGame,
  createWheelGame,
  getContestLeaderBoardById,
  getGameById,
  getListGame,
  getListWheelGameByGameId,
  getListWheelGameUser,
  getTotalGame,
  getTotalWheelGameByGameId,
  getWheelGameById,
  patchGameById,
  patchWheelGameById,
  removeGameById,
  removeManyGameByIds,
  removeWheelGameById,
} from '../apis'
import {
  DETAIL_GAME,
  DETAIL_WHEEL_GAME,
  LIST_GAME,
  LIST_GAME_INFINITE,
  LIST_WHEEL_GAME,
  LIST_HISTORY_WHEEL_GAME_USER,
  LIST_LEADER_BOARD,
  TOTAL_GAME,
  TOTAL_WHEEL_GAME_BY_GAME,
} from '../keys'

/**
 *
 * @method useMutationCreateGame
 * @returns
 */
export const useMutationCreateGame = () =>
  useMutation(createGame, {
    onSuccess: (res: TResApi<TGame>) => {
      queryClient.invalidateQueries([LIST_GAME]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * @method useQueryListGame
 * @param {TQueryGame}params
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryListGame = (params: TQueryGame, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TGame[]>>(
    [LIST_GAME, JSON.stringify(params), lang],
    () => getListGame(params, accessToken, lang),
    { enabled: !!accessToken },
  )
}

/**
 * @method useQueryListLeaderBoardById
 * @param {string}id
 * @param {TQueryParamsGetData}params
 * @returns
 */
export const useQueryListLeaderBoardById = (id: string, params: TQueryLoaderByBoardGame) => {
  const accessToken = checkAuth()
  return useQuery<TResDataListApi<TLeaderboard[]>>(
    [LIST_LEADER_BOARD, id, JSON.stringify(params)],
    () => getContestLeaderBoardById(id, params),
    { enabled: !!accessToken },
  )
}

/**
 * @method useQueryGameTotal
 * @param {string}token
 * @returns
 */
export const useQueryGameTotal = (token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<number>>([TOTAL_GAME], () => getTotalGame(token), { enabled: !!accessToken })
}

/**
 * @method useQueryGameById
 * @param {string}id
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryGameById = (id: string, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TGame>>([DETAIL_GAME, id, lang], () => getGameById(id, token, lang), {
    enabled: !!accessToken && !!id,
  })
}

/**
 *
 * @method useMutationPatchGameById
 * @returns
 */
export const useMutationPatchGameById = () =>
  useMutation(({ id, data }: { id: string; data: TPatchGame }) => patchGameById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.invalidateQueries([LIST_GAME]).then()
      queryClient.invalidateQueries([DETAIL_GAME, id]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemoveGameById
 * @returns
 */
export const useMutationRemoveGameById = () =>
  useMutation((id: string) => removeGameById(id), {
    onSuccess: (res: TResApi, id) => {
      queryClient.invalidateQueries([LIST_GAME]).then()
      queryClient.removeQueries([DETAIL_GAME, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemoveManyGameByIds
 * @returns
 */
export const useMutationRemoveManyGameByIds = () =>
  useMutation(removeManyGameByIds, {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      queryClient.refetchQueries(LIST_GAME).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

export const useInfinteQueryListGame = (params: TQueryGame, token?: string) => {
  const accessToken = token || checkAuth()

  return useInfiniteQuery<TResDataListApi<TGame[]>>(
    [LIST_GAME_INFINITE, JSON.stringify(params)],
    ({ pageParam = 1 }) => getListGame({ ...params, page: pageParam }, accessToken),
    {
      refetchOnWindowFocus: true,
      retry: 2,
      enabled: !!accessToken,
      getNextPageParam: (lastPage) =>
        lastPage?.page < Math.ceil(lastPage.total / lastPage.limit) ? lastPage.page + 1 : undefined,
    },
  )
}

// Wheel Game

/**
 * @method useQueryListWheelGameUser
 * @param {TQueryWheelGameUser}params
 * @param {string}token
 * @returns
 */
export const useQueryListWheelGameUser = (params: TQueryWheelGameUser, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TWheelGameUser[]>>(
    [LIST_HISTORY_WHEEL_GAME_USER, JSON.stringify(params)],
    () => getListWheelGameUser(params),
    { enabled: !!accessToken, refetchOnWindowFocus: true, retry: 2 },
  )
}

/**
 *
 * @param {TQueryWheelGame}params
 * @param {string}gameId
 * @param {string}token
 * @returns
 */
export const useQueryListWheelGameByGameId = (params: TQueryWheelGame, gameId: string, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TWheelGameItem[]>>(
    [LIST_WHEEL_GAME, JSON.stringify(params), gameId],
    () => getListWheelGameByGameId(params, gameId),
    { enabled: !!accessToken && !!gameId, refetchOnWindowFocus: true, retry: 2 },
  )
}

/**
 *
 * @method useMutationCreateWheelGame
 * @returns
 */
export const useMutationCreateWheelGame = () =>
  useMutation(createWheelGame, {
    onSuccess: (res: TResApi<TWheelGame>) => {
      queryClient.invalidateQueries([LIST_WHEEL_GAME]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationPatchWheelGameById
 * @returns
 */
export const useMutationPatchWheelGameById = () =>
  useMutation(({ id, data }: { id: string; data: TPatchWheelGame }) => patchWheelGameById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.refetchQueries([DETAIL_WHEEL_GAME, id]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * @method useQueryWheelGameById
 * @param {string}id
 * @returns
 */
export const useQueryWheelGameById = (id: string) => {
  const accessToken = checkAuth()
  return useQuery<TResApi<TWheelGame>>([DETAIL_WHEEL_GAME, id], () => getWheelGameById(id), {
    enabled: !!accessToken && !!id,
  })
}

/**
 *
 * @method useMutationRemoveWheelGameById
 * @returns
 */
export const useMutationRemoveWheelGameById = () =>
  useMutation((id: string) => removeWheelGameById(id), {
    onSuccess: (res: TResApi, id) => {
      queryClient.removeQueries([DETAIL_WHEEL_GAME, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * @method useQueryWheelGameTotal
 * @returns
 */
export const useQueryWheelGameTotal = (gameId: string) => {
  const accessToken = checkAuth()
  return useQuery<TResApi<number>>([TOTAL_WHEEL_GAME_BY_GAME, gameId], () => getTotalWheelGameByGameId(gameId), {
    enabled: !!accessToken && !!gameId,
  })
}
